import React, { useMemo, useState } from 'react';
import { Selection, TableBody } from 'react-aria-components';

import { Table, Cell, Column, Row, TableHeader } from '@/components/v2/Table';
import { isWithin24Hours } from '@/utils/utils';
import { ConferenceUserAssignment } from '../api/conferenceuserassignments/getConferenceUserAssignments';
import { Button } from '@/components/v2/Button';
import { useDeleteConferenceUserAssignment } from '../api/conferenceuserassignments/deleteConferenceUserAssignment';
import OncolensToast from '@/components/OncolensToast';
import { InfiniteData } from 'react-query';

export const ConferenceAccessTable = ({
  data,
}: {
  data: InfiniteData<ConferenceUserAssignment[]> | undefined;
}) => {
  const items = data?.pages[0].map((item) => ({
    id: item.id,
    hostOrganization: item.hostOrganization,
    conferenceName: item.conferenceName,
    addedOn: item.addedOn,
  }));

  const [sortDescriptor, setSortDescriptor] = useState({
    column: '',
    direction: 'ascending',
  });

  const [selectedItems, setSelectedItems] = useState<Selection>(new Set());
  const deleteConferenceUserAssignment = useDeleteConferenceUserAssignment();

  const sortedItems = useMemo(() => {
    const newItems = items?.filter((item) => isWithin24Hours(item.addedOn));
    // sort rest of itemns alphabetically by hostOrganization
    const restOfItems = items
      ?.filter((item) => !isWithin24Hours(item.addedOn))
      .sort((a, b) => {
        const valueA = a.hostOrganization;
        const valueB = b.hostOrganization;

        let comparison = valueA.localeCompare(valueB);

        if (sortDescriptor.direction === 'descending') {
          comparison *= -1;
        }

        return comparison;
      });
    const combinedItems = newItems && restOfItems ? [...newItems, ...restOfItems] : restOfItems;
    if (sortDescriptor.column === '' || !items) return combinedItems;

    const sortedItems = [...items].sort((a, b) => {
      const valueA = (a as any)[sortDescriptor.column];
      const valueB = (b as any)[sortDescriptor.column];

      let comparison = valueA.localeCompare(valueB);

      if (sortDescriptor.direction === 'descending') {
        comparison *= -1;
      }

      return comparison;
    });
    return sortedItems;
  }, [sortDescriptor, items]);

  const handleDeleteItems = () => {
    if (items && selectedItems) {
      const selectedItemIds =
        selectedItems === 'all' ? items.map((item) => item.id) : Array.from(selectedItems);
      Promise.all(
        selectedItemIds.map((conferenceUserAssignmentId) =>
          deleteConferenceUserAssignment.mutateAsync(conferenceUserAssignmentId as string),
        ),
      ).then(() => {
        setSelectedItems(new Set());
        OncolensToast.success('Conference access removed successfully');
      });
    }
  };

  const hasItemsSelected = selectedItems === 'all' || selectedItems.size > 0;

  return (
    <div className="px-2 pt-4 w-full">
      <Table
        // @ts-ignore
        allowsDragging={false}
        aria-label="Conference access"
        selectionMode="multiple"
        sortDescriptor={sortDescriptor as any}
        onSortChange={setSortDescriptor as any}
        selectedKeys={selectedItems}
        onSelectionChange={setSelectedItems}>
        <TableHeader>
          <Column width={250} id="hostOrganization" isRowHeader allowsSorting>
            Host organization
          </Column>
          <Column
            id="conferenceName"
            allowsSorting
            rightContent={
              <Button
                variant="link"
                disabled={!hasItemsSelected}
                onClickHandler={handleDeleteItems}>
                Remove access to conference
              </Button>
            }>
            Conference name
          </Column>
        </TableHeader>
        <TableBody items={sortedItems}>
          {(row) => (
            <Row>
              <Cell>{row.hostOrganization}</Cell>
              <Cell>
                {' '}
                <div className="flex items-center gap-1">
                  {isWithin24Hours(row.addedOn) && (
                    <div className="bg-sky-700 mr-1 uppercase text-white font-bold rounded-sm px-2 py-0.5 text-[10px]">
                      new
                    </div>
                  )}

                  {row.conferenceName}
                </div>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
